// Reusable design tokens & helpers for the CJ app.
import tcLogo from "../assets/tc-logo.png";

// Branding, change this single value to rename the app everywhere it appears.
export const APP_NAME = "CJ";
export const APP_TAGLINE = "Where Impact Shows Up!";
export const APP_LONG_NAME = "CJ, Where Impact Shows Up by True Colors International";

// CJ mascot avatar (welcomes users, shown on login + share intro)
export const CJ_AVATAR_URL = "/cj/cj_v2_3d.png";

// Secondary co-brand logo (kept for footer/admin chrome). Bundled locally so
// the header doesn't show a broken-image placeholder if the CDN hiccups
// (was breaking on iOS Safari intermittently).
export const TC_LOGO_URL = tcLogo;

// Freebie offered after a public testimonial submission
export const FREEBIE_PDF_URL = "https://info.truecolorsintl.com/hubfs/TC%20Recognizing%20Each%20Color%20Download.pdf-1.pdf";
export const FREEBIE_TITLE = "Recognizing Each Color";

export const ARCHETYPES = {
  gold:   { key: "gold",   title: "The Organizer",  color: "#ffd400", soft: "#FFF7CC", text: "#7A5A00" },
  blue:   { key: "blue",   title: "The Connector",  color: "#0091be", soft: "#D2EEF6", text: "#005A78" },
  orange: { key: "orange", title: "The Energizer",  color: "#f68b1f", soft: "#FEE8D3", text: "#8A4900" },
  green:  { key: "green",  title: "The Strategist", color: "#7ac143", soft: "#E5F4D8", text: "#3E6A1E" },
};

export const CHALLENGE_CATEGORIES = [
  "Communication breakdown",
  "Leadership gaps",
  "Team conflict",
  "Culture issues",
  "High turnover",
  "Low engagement",
];

export const INDUSTRIES = [
  "Corporate",
  "Non-Profit",
  "Education K-12",
  "Higher Education",
  "Healthcare",
  "Military",
  "Government",
  "Independent Business Owner",
];

export const COMPANY_SIZES = ["1-50", "51-200", "201-500", "501-1,000", "1,001-5,000", "5,000+"];

export const OUTCOME_CATEGORIES = [
  "Retention",
  "Engagement",
  "Productivity",
  "Conflict reduction",
  "Leadership growth",
  "Culture shift",
];

export const TRUE_COLORS_SOLUTIONS = [
  "Personal Awareness and Success Training",
  "Personal Awareness and Success Certification",
  "Communication Training",
  "Conflict Navigation Training",
  "Team Building",
  "Teaching and Learning Styles Training",
  "Emotional Intelligence Program",
  "Connected Leadership Program",
  "Educational Leadership Institute",
  "ConnectED",
  "Customer Service Skills for Superior Service",
  "Mastering Customer Connections",
  "True Colors Live Show",
  "Talent Acquisition Program",
  "One Hour Team Assessment Review",
];

export const RECOMMENDATION_OPTIONS = [
  { value: "definitely_yes", label: "Definitely yes!",     blurb: "I'd tell them to do it in a heartbeat.", color: "#7ac143" },
  { value: "maybe",          label: "Maybe",               blurb: "Depends on the team or moment.",          color: "#ffd400" },
  { value: "no",             label: "Unfortunately, no",   blurb: "We'd love to hear why so we can grow.",   color: "#f68b1f" },
];

export const PROOF_POINTS = [
  "Verbal testimonial from a leader",
  "Written quote / email / note received",
  "Repeat or expanded engagement signed",
  "Referral to another organization",
  "Visible behavior change observed",
  "Internal champion emerged",
  "Crisis or relationship saved",
  "Adopted across multiple teams or locations",
  "Hard metrics tracked in our ROI report",
];

export const CONSENT_OPTIONS = [
  { value: "approved_named", label: "Approved, by name" },
  { value: "approved_anonymized", label: "Approved, anonymized" },
  { value: "not_yet_asked", label: "Not yet asked" },
];

export const BADGES = {
  first_story:       { label: "First Story",     emoji: "✦", color: "#f68b1f" },
  five_stories:      { label: "5-Story Streak",  emoji: "✦", color: "#ffd400" },
  ten_stories:       { label: "10-Story Legend", emoji: "✦", color: "#7ac143" },
  streak_3:          { label: "3-Day Streak",    emoji: "✦", color: "#0091be" },
  streak_7:          { label: "7-Day Streak",    emoji: "✦", color: "#f68b1f" },
  first_approved:    { label: "Approved!",       emoji: "✦", color: "#7ac143" },
  archetype_gold:    { label: "Gold Badge",      emoji: "✦", color: "#ffd400" },
  archetype_blue:    { label: "Blue Badge",      emoji: "✦", color: "#0091be" },
  archetype_orange:  { label: "Orange Badge",    emoji: "✦", color: "#f68b1f" },
  archetype_green:   { label: "Green Badge",     emoji: "✦", color: "#7ac143" },
};

export const CONGRATS_MESSAGES = [
  { title: "Story logged.", body: "Another piece of the True Colors story bank just got stronger because of you." },
  { title: "Bookmark this win.", body: "That's exactly the kind of proof point our pipeline lights up over." },
  { title: "You're a momentum machine.", body: "This story is going to land in someone's call this week. Watch." },
  { title: "Receipts captured.", body: "Real outcomes, real teams. This is how we sell the work, not just the workshop." },
  { title: "Field-tested gold.", body: "Future-you on a discovery call just got a much better answer." },
  { title: "That's a closer.", body: "Imagine pulling this exact story up mid-pitch. Now you can." },
  { title: "True Colors moment.", body: "You just made the next sales conversation 30% easier." },
];

export const BONUS_MESSAGES = {
  outcomes:     "Proof points captured. +50",
  quote:        "Quote captured. +25",
  media:        "Media attached. +25",
  draft_return: "Welcome back. Bonus for returning. +30",
  submit_complete: "Story complete. +100",
};

// ----- Location options (used in capture + share + library filters) -----

export const COUNTRIES = [
  "United States",
  "Canada",
  "Mexico",
  "United Kingdom",
  "Ireland",
  "Australia",
  "New Zealand",
  "South Africa",
  "Singapore",
  "United Arab Emirates",
  "Other",
];

export const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas",
  "Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York",
  "North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina",
  "South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming","District of Columbia","Puerto Rico",
];

// ----- Kudos system (Phase 2, preset categories, no cap, +5 pts each) -----

export const KUDOS_CATEGORIES = [
  { key: "closed_deal", label: "Helped me close a deal",  icon: "🤝", color: "#f68b1f" },
  { key: "great_story", label: "Obtained a great story",  icon: "📚", color: "#0091be" },
  { key: "spoke_up",    label: "Spoke up in a meeting",   icon: "📣", color: "#ffd400" },
  { key: "taught",      label: "Taught me something",     icon: "💡", color: "#7ac143" },
  { key: "great_idea",  label: "Shared a great idea",     icon: "✨", color: "#083050" },
];
export const KUDOS_POINT_VALUE = 5;
